import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MessagesSquare, RefreshCw, ArrowRight, Loader2, Eye, MailCheck } from "lucide-react";
import { ChatTranscriptDialog } from "./ChatTranscriptDialog";
import { HotLeadAlertSettings } from "./HotLeadAlertSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY = {
  chaud: { label: "Chaud", cls: "bg-red-100 text-red-700 border-red-200" },
  tiede: { label: "Tiède", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  froid: { label: "Froid", cls: "bg-sky-100 text-sky-700 border-sky-200" },
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

const SummaryRow = ({ conv, onOpen }) => {
  const s = conv.summary;
  const p = PRIORITY[s?.priorite];
  return (
    <div className="p-4 rounded-xl border border-slate-100 bg-white hover:shadow-sm transition-shadow" data-testid={`chat-summary-${conv.session_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          {p ? (
            <Badge variant="outline" className={p.cls} data-testid={`chat-summary-priority-${conv.session_id}`}>{p.label}</Badge>
          ) : (
            <Badge variant="outline" className="text-slate-500">Analyse en cours</Badge>
          )}
          <span className="text-xs text-slate-400">{conv.messages_count || 0} messages · {formatDate(conv.last_message_at)}</span>
        </div>
        <div className="flex items-center gap-2">
          {conv.hot_alert_sent && (
            <span className="flex items-center gap-1 text-xs text-emerald-600" data-testid={`chat-summary-alert-sent-${conv.session_id}`}>
              <MailCheck className="w-3.5 h-3.5" /> Alerte envoyée
            </span>
          )}
          <Button variant="ghost" size="sm" onClick={() => onOpen(conv.session_id)} data-testid={`chat-summary-open-${conv.session_id}`}>
            <Eye className="w-4 h-4 mr-1" /> Voir la conversation
          </Button>
        </div>
      </div>
      {s ? (
        <>
          <p className="text-sm text-slate-700 mb-2">{s.resume}</p>
          <p className="text-xs text-slate-500 mb-2"><span className="font-semibold text-slate-700">Besoin :</span> {s.besoin}</p>
          <div className="flex items-start gap-2 text-sm bg-[#102692]/5 text-[#102692] rounded-lg px-3 py-2">
            <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span data-testid={`chat-summary-action-${conv.session_id}`}>{s.prochaine_action}</span>
          </div>
        </>
      ) : (
        <p className="text-sm text-slate-400">Le résumé sera créé 2 minutes après le dernier message du prospect.</p>
      )}
    </div>
  );
};

export const ChatSummaries = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ai/admin/conversations`);
      setConversations(res.data.conversations || []);
    } catch (e) {
      console.error("Conversations load error:", e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <Card className="border-0 shadow-sm" data-testid="chat-summaries-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <MessagesSquare className="w-5 h-5 text-[#102692]" />
            Conversations Sophie — Résumés Claude
          </CardTitle>
          <CardDescription>Résumé, besoin, prochaine action et priorité de chaque conversation</CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="chat-summaries-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3 max-h-[640px] overflow-y-auto">
        <HotLeadAlertSettings />
        {!loading && conversations.length === 0 && (
          <p className="text-center py-8 text-sm text-slate-500" data-testid="chat-summaries-empty">Aucune conversation pour le moment</p>
        )}
        {conversations.map((c) => <SummaryRow key={c.session_id} conv={c} onOpen={setOpenId} />)}
        <ChatTranscriptDialog sessionId={openId} onClose={() => setOpenId(null)} />
      </CardContent>
    </Card>
  );
};
